import React, { useState, useRef, useEffect } from 'react';
import cn from 'classnames';
import { Cropper, CropperPreview, CircleStencil } from 'react-advanced-cropper';
import AdjustablePreviewBackground from './AdjustablePreviewBackground';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import 'react-advanced-cropper/dist/style.css';
import './ImageEditor.scss';
import Button from './Button';
import Navigation from './Navigation';
import ResetIcon from './Icons/ResetIcon';
import AdjustableCropperBackground from './AdjustableCropperBackground';
import Slider from './Slider';
import useWindowSize from "../../hooks/useWindowSize";
import {
    getImagePreparationErrorMessage,
    prepareImageFileForUpload,
} from '../../utils/imageUploadPreparation';

// The polyfill for Safari browser. The dynamic require is needed to work with SSR
if (typeof window !== 'undefined') {
	require('context-filter-polyfill');
}

const AvatarImageCropper = ({ updateAvatar, closeModal, initialImage, aspectRatio, stencilWidth, stencilHeight, previousCrop }) => {
    const cropperRef = useRef(null);
    const previewRef = useRef(null);

    const [src, setSrc] = useState(initialImage ? URL.createObjectURL(initialImage) : null);
    const [mode, setMode] = useState('crop');
    const [adjustments, setAdjustments] = useState({
        brightness: 0,
        hue: 0,
        saturation: 0,
        contrast: 0,
    });
    const [error, setError] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const windowSize = useWindowSize();
    const isMobile = windowSize.width <= 768;

    useEffect(() => {
        return () => {
            if (src instanceof File) {
                URL.revokeObjectURL(src);
            }
        };
    }, [src]);

    const onChangeValue = (value) => {
        if (mode in adjustments) {
            setAdjustments((previousValue) => ({
                ...previousValue,
                [mode]: value,
            }));
        }
    };

    const onReset = () => {
        setMode('crop');
        setAdjustments({
            brightness: 0,
            hue: 0,
            saturation: 0,
            contrast: 0,
        });
    };

    const handleCropImage = () => {
        if (cropperRef.current && cropperRef.current.getCanvas) {
            const canvas = cropperRef.current.getCanvas();
            if (canvas) {
                canvas.toBlob((blob) => {
                    if (blob) {
                        const croppedFile = new File([blob], initialImage.name || 'cropped_image', { type: blob.type });
                        // Set the cropped image as the new source for further editing
                        setSrc(URL.createObjectURL(croppedFile));
                    }
                });
            }
        }
    };

    const handleSaveImage = () => {
        if (isSaving) {
            return;
        }

        if (cropperRef.current && cropperRef.current.getCanvas) {
            const canvas = cropperRef.current.getCanvas();
            if (canvas) {
                canvas.toBlob(async (blob) => {
                    if (blob) {
                        try {
                            setIsSaving(true);
                            const rawEditedFile = new File([blob], initialImage.name || 'edited_image.jpg', { type: blob.type || 'image/jpeg' });
                            const editedFile = await prepareImageFileForUpload(rawEditedFile, {
                                forceOptimization: true,
                                maxDimension: 1200,
                            });
                            await updateAvatar(editedFile);
                            closeModal();
                        } catch (error) {
                            console.error('Error preparing cropped avatar:', error);
                            setError(error?.response?.data?.message || getImagePreparationErrorMessage(error));
                        } finally {
                            setIsSaving(false);
                        }
                    }
                }, 'image/jpeg', 0.9);
            }
        }
    };

    const onUpdate = (cropper) => {
        previewRef.current?.update(cropper);
    };

    const changed = Object.values(adjustments).some((el) => Math.floor(el * 100));
    const cropperEnabled = mode === 'crop';

    return (
        <div className={'image-editor'}>
            {error && (
                <div className="image-editor__error-container">
                    <div className="image-editor__error-icon">
                        <ExclamationTriangleIcon aria-hidden="true" className="image-editor__error-icon-img" />
                    </div>
                    <div className="image-editor__error-content">
                        <h3 className="image-editor__error-heading">Attention needed</h3>
                        <p className="image-editor__error-message">{error}</p>
                    </div>
                </div>
            )}
            <div className="image-editor__cropper">
                <Cropper
                    src={src}
                    stencilComponent={CircleStencil}
                    ref={cropperRef}
                    imageRestriction={isMobile ? "none" : "fitArea"}
                    stencilProps={{
                        movable: !isMobile && cropperEnabled,  
                        resizable: !isMobile && cropperEnabled,
                        lines: cropperEnabled,
                        handlers: !isMobile && cropperEnabled,
                        aspectRatio: aspectRatio, 
                        width: stencilWidth,
                        height: stencilHeight,
                        overlayClassName: cn(
                            'image-editor__cropper-overlay',
                            !cropperEnabled && 'image-editor__cropper-overlay--faded',
                        ),
                    }}
                    backgroundWrapperProps={{
                        scaleImage: cropperEnabled,
                        moveImage: cropperEnabled,
                    }}
                    backgroundComponent={AdjustableCropperBackground}
                    backgroundProps={adjustments}
                    onUpdate={onUpdate}
                />
                {mode !== 'crop' && (
                    <Slider
                        className="image-editor__slider"
                        value={adjustments[mode]}
                        onChange={onChangeValue}
                    />
                )}
                
                <button
                    className={cn('image-editor__reset-button', !changed && 'image-editor__reset-button--hidden')}
                    onClick={onReset}
                >
                    <ResetIcon />
                </button>
            </div>
            <div className="image-editor__actions">
            <button
                type="button"
                className="image-cropper__cancel-button"
                onClick={closeModal}
                disabled={isSaving}
                >
                Cancel
                </button>
            <button
                type="button" 
                className="image-cropper__save-button"
                onClick={handleSaveImage}
                disabled={isSaving}
                >
                {isSaving ? 'Saving...' : 'Save Image'}
                </button>
            </div>
            <Navigation mode={mode} onChange={setMode} />
        </div>
    );
};

export default AvatarImageCropper;



